// ---------------------------------------------------------------------------
// C4+C5+C6: Form Intelligence — auto-CRM mapping, automation suggestions, qualifying
// ---------------------------------------------------------------------------
// C4: Extend auto-wirer pattern — form fields → CRM fields with confidence.
// C5: Suggest automations when form is wired (welcome sequence, notification, pipeline).
// C6: Conditional fields + progressive profiling + lead scoring per answer.
// ---------------------------------------------------------------------------

export interface FormField {
  id: string;
  label: string;
  type: string; // text, email, phone, select, textarea, checkbox, radio
  required?: boolean;
  options?: string[]; // For select/radio
}

export interface FieldMapping {
  fieldId: string;
  fieldLabel: string;
  crmField: string; // e.g., "people.email", "people.phone", "deals.value"
  confidence: number; // 0-100
  status: "mapped" | "unmapped" | "manual";
}

export interface AutomationSuggestion {
  id: string;
  name: string;
  description: string;
  type: "welcome_sequence" | "lead_notification" | "crm_pipeline" | "tag_assignment" | "score_update";
  config: Record<string, unknown>;
}

export interface ConditionalRule {
  sourceFieldId: string;
  sourceValue: string;
  targetFieldId: string;
  action: "show" | "hide" | "require";
}

export interface ScoreContribution {
  fieldId: string;
  value: string;
  points: number;
}

// --- C4: Auto-CRM Field Mapping ---

const CRM_FIELD_PATTERNS: Array<{ pattern: RegExp; crmField: string; confidence: number }> = [
  { pattern: /^e-?mail$/i, crmField: "people.email", confidence: 99 },
  { pattern: /email|e-mail/i, crmField: "people.email", confidence: 95 },
  { pattern: /^(first|given)\s*name$/i, crmField: "people.first_name", confidence: 98 },
  { pattern: /^(last|family|sur)\s*name$/i, crmField: "people.last_name", confidence: 98 },
  { pattern: /^name$|^full\s*name$/i, crmField: "people.first_name", confidence: 85 },
  { pattern: /phone|tel|mobile|cell/i, crmField: "people.phone", confidence: 92 },
  { pattern: /company|business|organization/i, crmField: "people.company", confidence: 90 },
  { pattern: /website|url|site/i, crmField: "people.website", confidence: 88 },
  { pattern: /address|street/i, crmField: "people.address", confidence: 85 },
  { pattern: /city|town/i, crmField: "people.city", confidence: 87 },
  { pattern: /state|province|region/i, crmField: "people.state", confidence: 85 },
  { pattern: /zip|postal|postcode/i, crmField: "people.zip", confidence: 90 },
  { pattern: /country/i, crmField: "people.country", confidence: 88 },
  { pattern: /budget|spend/i, crmField: "deals.value", confidence: 75 },
  { pattern: /message|comment|note|detail/i, crmField: "activities.note", confidence: 70 },
  { pattern: /service|interest|looking\s*for/i, crmField: "deals.description", confidence: 65 },
  { pattern: /how\s*did\s*you|referral|source/i, crmField: "people.source", confidence: 80 },
];

/** Auto-map form fields to CRM fields with confidence scores */
export function autoMapFormFields(fields: FormField[]): FieldMapping[] {
  return fields.map(field => {
    const label = field.label.toLowerCase().trim();

    // Try type-based mapping first (email type → people.email)
    if (field.type === "email") {
      return { fieldId: field.id, fieldLabel: field.label, crmField: "people.email", confidence: 99, status: "mapped" as const };
    }
    if (field.type === "phone" || field.type === "tel") {
      return { fieldId: field.id, fieldLabel: field.label, crmField: "people.phone", confidence: 95, status: "mapped" as const };
    }

    // Try label-based pattern matching
    for (const { pattern, crmField, confidence } of CRM_FIELD_PATTERNS) {
      if (pattern.test(label) || pattern.test(field.label)) {
        return { fieldId: field.id, fieldLabel: field.label, crmField, confidence, status: "mapped" as const };
      }
    }

    // Unmapped
    return { fieldId: field.id, fieldLabel: field.label, crmField: "", confidence: 0, status: "unmapped" as const };
  });
}

// --- C5: Automation Suggestions ---

/** Suggest automations when a form is wired to CRM */
export function suggestAutomations(formFields: FormField[], mappings: FieldMapping[]): AutomationSuggestion[] {
  const suggestions: AutomationSuggestion[] = [];
  const hasEmail = mappings.some(m => m.crmField === "people.email" && m.confidence > 80);
  const hasPhone = mappings.some(m => m.crmField === "people.phone" && m.confidence > 80);
  const hasBudget = mappings.some(m => m.crmField === "deals.value");

  // Welcome sequence (if email mapped)
  if (hasEmail) {
    suggestions.push({
      id: "welcome_sequence",
      name: "Welcome Sequence",
      description: `5-day email sequence: Day 1 welcome → Day 2 value → Day 3 social proof → Day 5 offer${hasPhone ? " + SMS on Day 3" : ""}`,
      type: "welcome_sequence",
      config: { trigger: "form.submitted", steps: 5, channelEmail: true, channelSms: hasPhone, delayDays: [0, 1, 2, 4, 6] },
    });
  }

  // Lead notification (always suggested)
  suggestions.push({
    id: "lead_notification",
    name: "Lead Notification",
    description: "Instant email + push notification to you when someone fills out this form",
    type: "lead_notification",
    config: { trigger: "form.submitted", notifyEmail: true, notifyPush: true },
  });

  // CRM pipeline (if enough info to create a deal)
  if (hasEmail || hasPhone) {
    suggestions.push({
      id: "crm_pipeline",
      name: "Auto-Create Deal",
      description: `Create a deal in your pipeline when this form is submitted${hasBudget ? " (budget as deal value)" : ""}`,
      type: "crm_pipeline",
      config: { trigger: "form.submitted", createDeal: true, pipelineStage: "new_lead", useValueField: hasBudget },
    });
  }

  return suggestions;
}

// --- C6: Qualifying Form Logic ---

/** Generate conditional rules based on form field analysis */
export function suggestConditionalRules(fields: FormField[]): ConditionalRule[] {
  const rules: ConditionalRule[] = [];

  // Find select/radio fields that could trigger conditional display
  const selectFields = fields.filter(f => f.type === "select" || f.type === "radio");
  const additionalFields = fields.filter(f => f.type === "textarea" || f.type === "text");

  for (const selectField of selectFields) {
    // If a select field has "other" option, suggest showing a text field
    if (selectField.options?.some(o => /other|custom|different/i.test(o))) {
      const textField = additionalFields.find(f => /detail|specify|describe/i.test(f.label));
      if (textField) {
        rules.push({
          sourceFieldId: selectField.id,
          sourceValue: selectField.options?.find(o => /other/i.test(o)) ?? "Other",
          targetFieldId: textField.id,
          action: "show",
        });
      }
    }
  }

  return rules;
}

/** Progressive profiling config — fewer fields on first visit */
export interface ProgressiveProfile {
  firstVisitFields: string[]; // Field IDs to show on first visit (2-3 fields)
  returnVisitFields: string[]; // Additional fields for return visitors
}

export function suggestProgressiveProfile(fields: FormField[]): ProgressiveProfile {
  // Priority: email first, then name, then everything else on return
  const emailField = fields.find(f => f.type === "email" || /email/i.test(f.label));
  const nameField = fields.find(f => /name/i.test(f.label) && !/last|company/i.test(f.label));
  const phoneField = fields.find(f => f.type === "phone" || /phone/i.test(f.label));

  const firstVisitIds: string[] = [];
  if (emailField) firstVisitIds.push(emailField.id);
  if (nameField) firstVisitIds.push(nameField.id);
  if (firstVisitIds.length < 2 && phoneField) firstVisitIds.push(phoneField.id);

  const returnVisitIds = fields
    .filter(f => !firstVisitIds.includes(f.id))
    .map(f => f.id);

  return { firstVisitFields: firstVisitIds, returnVisitFields: returnVisitIds };
}

/** Score contribution per form answer (for lead scoring) */
export function suggestScoreContributions(fields: FormField[]): ScoreContribution[] {
  const contributions: ScoreContribution[] = [];

  for (const field of fields) {
    if (field.type === "select" || field.type === "radio") {
      // Higher scores for "ready to buy" signals
      for (const option of field.options ?? []) {
        let points = 5; // Default
        if (/urgent|asap|immediately|this week/i.test(option)) points = 20;
        else if (/budget|ready|serious|enterprise/i.test(option)) points = 15;
        else if (/interested|exploring|learning/i.test(option)) points = 8;
        else if (/no|not yet|just browsing/i.test(option)) points = 2;
        contributions.push({ fieldId: field.id, value: option, points });
      }
    }
    // Required fields filled = base points
    if (field.required) {
      contributions.push({ fieldId: field.id, value: "__filled__", points: 3 });
    }
  }

  return contributions;
}
